import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApplicationForm, ApplicationFormData } from './ApplicationForm';
import CircularProgress from '@mui/material/CircularProgress';

interface EditDraftPageProps {
    onLogout: () => void;
}

// Заменили дефолтный IP на пустую строку, адрес берется строго из .env
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

type Program = {
  id: string;
  name: string;
  durationHours: number;
  isActive: boolean;
  externalUrl?: string;
  createdAt: string;
  updatedAt: string;
};

const parseDate = (value?: string | null): Date | null => {
    if (!value) return null;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeDateString = (value?: string | null): string | null => {
    if (!value) return null;
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return null;
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const formatPayload = (formData: ApplicationFormData) => {
    const organizationName = formData.organization?.fullName || formData.organization?.name || '';
    return {
        organization_id: formData.organization?.id ? Number(formData.organization.id) : null,
        responsible_specialist_id: formData.specialist?.id ? Number(formData.specialist.id) : null,
        subfolder_name: formData.subfolderName.trim() || null,
        exam_export_folder: formData.subfolderName.trim() || null,
        students: formData.students.map(student => ({
            full_name: student.full_name,
            email: student.email,
            organization: organizationName,
            programs: student.programs.map(pa => ({
                program_id: Number(pa.program.id),
                program_name: pa.program.name,
                theoretical_start_date: pa.theoretical_start_date || null,
                theoretical_end_date: pa.theoretical_end_date || null,
                exam_date: pa.exam_date || null,
            })),
        })),
        ui_state: formData
    };
};

const getFormSignature = (formData: ApplicationFormData): string => {
    return JSON.stringify({
        organizationId: formData.organization?.id ?? null,
        specialistId: formData.specialist?.id ?? null,
        subfolderName: formData.subfolderName.trim(),
        students: formData.students.map(s => ({
            id: s.id,
            full_name: s.full_name,
            email: s.email,
            programs: s.programs
                .map(p => ({
                    id: p.program.id,
                    start: p.theoretical_start_date ?? null,
                    end: p.theoretical_end_date ?? null,
                    exam: p.exam_date ?? null,
                }))
                .sort((a, b) => String(a.id).localeCompare(String(b.id))),
        })),
    });
};

const EditDraftPage: React.FC<EditDraftPageProps> = ({ onLogout }) => {
    const { draftId } = useParams<{ draftId: string }>();
    const navigate = useNavigate();
    const [initialData, setInitialData] = useState<ApplicationFormData | null>(null);
    const [loading, setLoading] = useState(true);

    const [commonProgramIds, setCommonProgramIds] = useState<Set<string>>();
    const [commonProgramDetails, setCommonProgramDetails] = useState<Record<string, any>>();
    const [commonPrograms, setCommonPrograms] = useState<Program[]>();
    const initialSignatureRef = useRef<string>('');

    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    const username = user?.full_name || user?.username || '—';

    let roleName = 'user';
    if (user?.role) {
        if (typeof user.role === 'string') {
            roleName = user.role;
        } else if (typeof user.role === 'object' && user.role.name) {
            roleName = user.role.name;
        }
    }

    useEffect(() => {
        const fetchDraft = async () => {
            const token = localStorage.getItem('token');
            try {
                const res = await fetch(`${API_BASE_URL}/api/v1/application-drafts/${draftId}`, {
                    headers: { "Authorization": token || '' }
                });

                if (res.status === 401) {
                    onLogout();
                    return;
                }

                if (res.ok) {
                    const data = await res.json();
                    if (data.data?.ui_state) {
                        const formData: ApplicationFormData = data.data.ui_state;
                        setInitialData(formData);

                        initialSignatureRef.current = getFormSignature(formData);

                        if (formData.students && formData.students.length > 0) {
                            const programInstances = new Map<string, {
                                program: Program;
                                dates: Array<{ start: string | null; end: string | null; exam: string | null }>;
                            }>();

                            formData.students.forEach((student) => {
                                student.programs.forEach((p) => {
                                    const progId = String(p.program.id);
                                    if (!programInstances.has(progId)) {
                                        programInstances.set(progId, {
                                            program: p.program,
                                            dates: []
                                        });
                                    }
                                    programInstances.get(progId)!.dates.push({
                                        start: normalizeDateString(p.theoretical_start_date),
                                        end: normalizeDateString(p.theoretical_end_date),
                                        exam: normalizeDateString(p.exam_date)
                                    });
                                });
                            });

                            const restoredIds = new Set<string>();
                            const restoredDetails: Record<string, any> = {};
                            const restoredPrograms: Program[] = [];

                            programInstances.forEach((inst, progId) => {
                                const firstDate = inst.dates[0];
                                const allDatesMatch = inst.dates.every(d =>
                                    d.start === firstDate.start &&
                                    d.end === firstDate.end &&
                                    d.exam === firstDate.exam
                                );

                                if (allDatesMatch) {
                                    restoredIds.add(progId);
                                    restoredPrograms.push(inst.program);
                                    restoredDetails[progId] = {
                                        theoretical_start_date: parseDate(firstDate.start),
                                        theoretical_end_date: parseDate(firstDate.end),
                                        exam_date: parseDate(firstDate.exam)
                                    };
                                }
                            });

                            if (restoredIds.size > 0) {
                                setCommonProgramIds(restoredIds);
                                setCommonProgramDetails(restoredDetails);
                                setCommonPrograms(restoredPrograms);
                            }
                        }
                    } else {
                        const emptyForm: ApplicationFormData = {
                            organization: null,
                            specialist: null,
                            subfolderName: data.data?.subfolder_name || '',
                            students: []
                        };
                        setInitialData(emptyForm);
                        initialSignatureRef.current = getFormSignature(emptyForm);
                    }
                }
            } catch (e) {
                console.error("Ошибка загрузки черновика", e);
            } finally {
                setLoading(false);
            }
        };
        fetchDraft();
    }, [draftId, onLogout]);

    const saveDraftData = async (formData: ApplicationFormData) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/v1/application-drafts/${draftId}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token || ''
            },
            body: JSON.stringify({ data: formatPayload(formData), last_step: "form" })
        });

        if (res.status === 401) throw new Error("Сессия истекла");
        if (!res.ok) throw new Error("Ошибка обновления черновика");

        initialSignatureRef.current = getFormSignature(formData);
        return await res.json();
    };

    const handleSaveDraft = async (formData: ApplicationFormData) => {
        const data = await saveDraftData(formData);
        navigate('/drafts');
        return data;
    };

    const handleAutoSaveDraft = useCallback((formData: ApplicationFormData) => {
        const currentSignature = getFormSignature(formData);

        if (currentSignature === initialSignatureRef.current) {
            return;
        }

        const token = localStorage.getItem('token');
        fetch(`${API_BASE_URL}/api/v1/application-drafts/${draftId}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token || ''
            },
            body: JSON.stringify({ data: formatPayload(formData), last_step: "form" }),
            keepalive: true
        }).catch(console.error);
    }, [draftId]);

    const handlePublish = async (formData: ApplicationFormData) => {
        const token = localStorage.getItem('token');

        await saveDraftData(formData);

        const res = await fetch(`${API_BASE_URL}/api/v1/application-drafts/${draftId}/publish`, {
            method: "POST",
            headers: { "Authorization": token || '' }
        });

        if (res.status === 401) throw new Error("Сессия истекла");
        if (!res.ok) {
            let msg = "Ошибка публикации";
            try {
                const err = await res.json();
                msg = err?.detail || msg;
            } catch(e) {}
            throw new Error(msg);
        }

        const data = await res.json();
        navigate('/applications');
        return data;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex justify-center items-center">
                <CircularProgress />
            </div>
        );
    }

    return (
        <ApplicationForm
            onLogout={onLogout}
            isEditing={true}
            initialData={initialData || undefined}
            onSubmit={handlePublish}
            onSaveDraft={handleSaveDraft}
            onAutoSaveDraft={handleAutoSaveDraft}
            username={username}
            role={roleName}
            initialCommonProgramIds={commonProgramIds}
            initialCommonProgramDetails={commonProgramDetails}
            initialCommonPrograms={commonPrograms}
        />
    );
};

export default EditDraftPage;